import { useCallback, useEffect } from 'react';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import { useQuery, ApolloError, QueryResult, OperationVariables } from '@apollo/client';
import type { AttendeeHubConfig as QueryAttendeeHubConfig } from '@graphql/gql/graphql';
import { GET_ATTENDEE_HUB_CONFIG } from '@graphql/operations/attendeeHubConfig/getAttendeeHubConfig';
import useLocaleForQueries from './useLocaleForQueries';
import { appVariables } from '../apollo/appVariables';

interface AttendeeHubData {
  getAttendeeHubConfig: QueryAttendeeHubConfig;
}

// This is the type of the "...rest" value we take from useQuery
type QueryWithoutDataAndError = Omit<QueryResult<AttendeeHubData, OperationVariables>, 'data' | 'error'>;

interface AttendeeHubConfigObject extends QueryWithoutDataAndError {
  error: ApolloError;
  isFeatureSettingEnabled: (featureCode: string) => boolean;
  getFeatureSettingMetadata: (featureCode: string) => Record<string, unknown>;
  mobileAssets: QueryAttendeeHubConfig['mobileAssets'];
  customPageSettings: QueryAttendeeHubConfig['customPageSettings'];
  pageBannerPageSettingsBulk: QueryAttendeeHubConfig['pageBannerPageSettingsBulk'];
  pageBannerSettings: QueryAttendeeHubConfig['pageBannerSettings'];
  morePageSettings: QueryAttendeeHubConfig['morePageSettings'] | Record<string, unknown>;
  attendeesVisible: QueryAttendeeHubConfig['privacySettings']['attendeesVisible'];
  documentSharingEnabled: QueryAttendeeHubConfig['securitySettings']['documentSharingEnabled'];
}
const LOG = LoggerFactory.create('useAttendeeHubConfig');

export const useAttendeeHubConfig = (): AttendeeHubConfigObject => {
  const { publicPage } = appVariables();
  const locale = useLocaleForQueries();
  const { data, error, loading, ...rest } = useQuery<AttendeeHubData>(GET_ATTENDEE_HUB_CONFIG, {
    variables: { locale },
    skip: publicPage
  });

  useEffect(() => {
    if (error) {
      LOG.error('Error fetching attendee hub configuration', error);
    }
  }, [error]);

  const {
    featureSettings = [],
    mobileAssets,
    customPageSettings = [],
    pageBannerPageSettingsBulk = [],
    pageBannerSettings = [],
    morePageSettings = {},
    privacySettings: { attendeesVisible } = { attendeesVisible: undefined },
    securitySettings: { documentSharingEnabled } = {
      documentSharingEnabled: undefined
    }
  } = data?.getAttendeeHubConfig || {};

  const isFeatureSettingEnabled = useCallback(
    (featureCode: string): boolean => {
      if (!featureCode) {
        return false;
      }
      return (featureSettings.find(config => config.id === featureCode) || {}).isEnabled || false;
    },
    [featureSettings]
  );

  const getFeatureSettingMetadata = useCallback(
    (featureId: string): Record<string, unknown> => {
      if (!featureId) {
        return {};
      }
      try {
        const metaString = (featureSettings.find(feature => feature.id === featureId) || {}).metadata || '{}';
        return JSON.parse(metaString);
      } catch {
        return {};
      }
    },
    [featureSettings]
  );

  return {
    ...rest,
    loading,
    error,
    isFeatureSettingEnabled,
    getFeatureSettingMetadata,
    mobileAssets,
    customPageSettings: customPageSettings || [],
    pageBannerPageSettingsBulk: pageBannerPageSettingsBulk || [],
    pageBannerSettings: pageBannerSettings || [],
    morePageSettings: morePageSettings || {},
    attendeesVisible,
    documentSharingEnabled
  };
};
